import {Injectable} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {catchError, filter, map, takeUntil} from 'rxjs/operators';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {select, Store} from '@ngrx/store';
import {environment} from '@env/environment';
import {User} from '@app/core/model/user';
import {ApplicationGalleryModel} from '@app/views/admin/application-settings/models/application-gallery.model';
import {ISubcategory} from '@app/core/comp/filtering/filter';
import {isAuthenticated, State} from '@app/app.reducers';
import {CustomerService} from '@app/core/service/customer.service';
import {DataSourceType} from '@app/views/admin/application-settings/models/data-source-type.model';
import {WebhookBanner} from '@app/views/admin/application-settings/models/provisioning.model';

export interface ISchoolPassportSsoAppModel {
    id: number;
    name: string;
    applicationAttribute: string;
    serviceName: string;
    idp?: boolean;
}

@Injectable()
export class AppGalleryService {
    user: User;

    static compareTextForSorting(nameApp1: string, nameApp2: string) {
        if (nameApp1 < nameApp2) {
            return -1;
        }
        if (nameApp1 > nameApp2) {
            return 1;
        }
        return 0;
    }

    constructor (private http: HttpClient,
                 private store: Store<State>
    ) {}

    getApplications(): Observable<ApplicationGalleryModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications`;
        return this.http
            .get<ApplicationGalleryModel[]>(url)
            .pipe(
                map((applications: ApplicationGalleryModel[]) =>
                    applications.filter((app: ApplicationGalleryModel) =>
                        CustomerService.checkIsSupportSsoAppFeature()
                        || (<DataSourceType>app.dataSourceType !== DataSourceType.Passport)
                    )
                ),
                catchError(this.handleError),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    getAllVendorsApplications(): Observable<ApplicationGalleryModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/vendor`;
        return this.http
            .get<ApplicationGalleryModel[]>(url)
            .pipe(
                catchError(this.handleError),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    public getDefaultCategories(): Observable<ISubcategory[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/categories`;

        return this.http
            .get<ISubcategory[]>(url)
            .pipe(
                catchError(this.handleError),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    public getSchoolPassportSsoApplications(): Observable<ISchoolPassportSsoAppModel[]> {
        const url = `${environment.etxBaseURI}/productprofile/getServices?serviceClass=ConnectorService`;

        if (CustomerService.checkIsSupportSsoAppFeature()) {
            return this.http
                .get<ISchoolPassportSsoAppModel[]>(url)
                .pipe(
                    catchError(this.handleError),
                    takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
                );
        } else {
            return of([]);
        }
    }

    getWebhookBanners(): Observable<WebhookBanner[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/trial`;
        return this.http
            .get<WebhookBanner[]>(url)
            .pipe(
                catchError(this.handleError),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(
                `Backend returned code ${error.status}, ` +
                `body was: ${error.message}`);
        }
        return throwError(
            'Something bad happened, please try again later.');
    }
}

export const DEFAULT_SCHOOL_PASSPORT_SSO_APPS_LIST = [
    'CS_TemplateLTI_SSO',
    'CS_TemplateOAuth_SSO',
    'CS_Template_SSO',
    'CS_TemplateSAML_SSO',
];
